import { useId, type ReactNode } from "react";
import { ResponsiveContainer, Tooltip, Treemap } from "recharts";
import { Box } from "../../components/ui/box";
import type { Omittable } from "../../lib/props";
import { useChartColors, type ChartColorToken } from "./colors";
import { CHART_MOTION, TooltipShell, useChartMotion } from "./internal/recharts";

/** One tile of the layout. */
export type TreemapChartTile = Omittable<{
  id?: string;
  label: string;
  value: number;
}>;

export type TreemapChartProps = Omittable<{
  data: readonly TreemapChartTile[];
  /** Index-aligned tile colors (`useCategoricalColors(...).tokens`) so color
   * follows the entity, never its layout position. */
  colorTokens: readonly ChartColorToken[];
  /** Print each tile's value under its label. */
  showValues?: boolean;
  /** How a printed value reads (`compactNumber`, a currency). Default: the raw number. */
  formatValue?: (value: number) => string;
  /** Gap between tiles, in px. */
  padding?: number;
  onTileClick?: (tile: TreemapChartTile) => void;
  /** Hover card content for the hovered tile; omit for no hover card. */
  renderTooltip?: (tile: TreemapChartTile) => ReactNode;
  ariaLabel?: string;
}>;

// The datum recharts lays out: the tile rides along so the layout pass cannot
// separate a tile from its color or identity (recharts re-orders nodes).
type TreemapDatum = {
  name: string;
  value: number;
  tile: TreemapChartTile;
  token: ChartColorToken | undefined;
};

// recharts clones this element once per laid-out node, merging the node's
// geometry and datum fields into its props — so the geometry is always present
// at run time and the guard below is for the types. Depth 0 is the layout
// root, not a tile.
function TreemapTile({
  x,
  y,
  width,
  height,
  depth,
  tile,
  token,
  showValues,
  formatValue,
  padding,
  interactive,
}: Omittable<{
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  depth?: number;
  tile?: TreemapChartTile;
  token?: ChartColorToken;
  showValues?: boolean;
  formatValue?: (value: number) => string;
  padding: number;
  interactive: boolean;
}>) {
  const c = useChartColors();
  const clipId = useId();
  if (
    x === undefined ||
    y === undefined ||
    width === undefined ||
    height === undefined ||
    depth === undefined ||
    depth < 1 ||
    tile === undefined
  )
    return null;

  // recharts' squarify has no gutter of its own, so each tile insets by half
  // the gap on every side — neighbors then sit one full gap apart.
  const inset = padding / 2;
  const w = Math.max(0, width - padding);
  const h = Math.max(0, height - padding);
  if (w <= 0 || h <= 0) return null;

  // Labels only where they fit without shouting over the layout.
  const roomy = w > 56 && h > 32;

  // The label paints `var(--c-foreground)` off the `data-color` cascade, so
  // the browser pairs readable ink to the tile fill and re-pairs on a theme
  // flip — nothing is measured in JS.
  return (
    <g
      data-color={token}
      transform={`translate(${x + inset},${y + inset})`}
      cursor={interactive ? "pointer" : undefined}
    >
      <rect width={w} height={h} rx={3} fill={c.paint(token)} />
      {roomy ? (
        <>
          <clipPath id={clipId}>
            <rect width={Math.max(0, w - 10)} height={h} />
          </clipPath>
          <g clipPath={`url(#${clipId})`}>
            <text x={7} y={16} fill="var(--c-foreground)" fontSize={12} fontWeight={600}>
              {tile.label}
            </text>
            {showValues ? (
              <text
                x={7}
                y={31}
                fill="var(--c-foreground)"
                fontSize={11}
                fontWeight={400}
              >
                {formatValue ? formatValue(tile.value) : tile.value}
              </text>
            ) : null}
          </g>
        </>
      ) : null}
    </g>
  );
}

/**
 * The finished treemap (recharts engine): squarified tiles sized by value,
 * entity-owned color riding each tile. Colors are semantic tokens painted as
 * live `var()` strings; recharts never crosses this surface.
 */
export function TreemapChart({
  data,
  colorTokens,
  showValues,
  formatValue,
  padding = 0,
  onTileClick,
  renderTooltip,
  ariaLabel,
}: TreemapChartProps) {
  const animate = useChartMotion();
  const rows: TreemapDatum[] = data.map((tile, i) => ({
    name: tile.label,
    value: tile.value,
    tile,
    token: colorTokens[i],
  }));

  return (
    <Box
      width="100%"
      height="100%"
      role={ariaLabel === undefined ? undefined : "img"}
      aria-label={ariaLabel}
    >
      <ResponsiveContainer width="100%" height="100%">
        <Treemap
          data={rows}
          isAnimationActive={animate}
          {...CHART_MOTION}
          {...(onTileClick
            ? {
                onClick: (node: { [k: string]: unknown }) => {
                  const tile = node.tile as TreemapChartTile | undefined;
                  if (tile !== undefined) onTileClick(tile);
                },
              }
            : null)}
          content={
            <TreemapTile
              showValues={showValues}
              formatValue={formatValue}
              padding={padding}
              interactive={onTileClick !== undefined}
            />
          }
        >
          {renderTooltip ? (
            <Tooltip
              isAnimationActive={false}
              content={({ active, payload }) => {
                const node = payload?.[0]?.payload as TreemapDatum | undefined;
                if (!active || node?.tile === undefined) return null;
                return <TooltipShell>{renderTooltip(node.tile)}</TooltipShell>;
              }}
            />
          ) : null}
        </Treemap>
      </ResponsiveContainer>
    </Box>
  );
}
